
<script setup>
import { computed, ref } from 'vue'
import { Format } from '@/models/utils/FormatUtils.js'

const props = defineProps({
	data: {
		type: Object,
		required: true
	},

	/**
	 * Set keys if there are values to ignore in the object
	 */
	keys: {
		type: Array,
		default: () => []
	},
	start_collapsed: {
		type: Boolean,
		default: true
	},
	/**
	 * Prevents the key column from getting very wide with long names, forces break
	 */
	narrow_key:{
		type: Boolean,
		default: false
	},
	hide_key:{
		type: Boolean,
		default: false
	},
	modal:{
		type: Boolean,
		default: false
	},
	allow_collapse:{
		type: Boolean,
		default: true
	}
})

const displayKeys = computed(() => {
	if (!props.data) return [];
	if (props.keys.length > 0) {
		return props.keys.filter(key => key in props.data)
	}
	return Object.keys(props.data)
})

const collapsed = ref(props.start_collapsed)

/**
 * Format the value so it easier to read
 * @param {*} p_value Raw cell value
 * @returns {string}
 */
function formatValue(p_value)
{
	if (p_value === null) return 'null';
	if (p_value === undefined) return 'undefined';
	if (typeof p_value === 'number') return String(p_value);
	if (typeof p_value === 'boolean') return String(p_value);

	if (p_value instanceof Date) return Format.date(p_value);

	//This will check the text for a date format and return a string if it doesn't match
	if (typeof p_value === 'string') return Format.formatDateString(p_value);

	if (typeof p_value === 'object') return JSON.stringify(p_value, null, 2);
	return String(p_value);
}

function toggle()
{
	collapsed.value = !collapsed.value
}
</script>

<template>
	<div :class="{'overflow-y-scroll': props.modal}">
		<table v-if="data != null" class="object-viewer !border !border-2 !border-opacity-10 !border-black min-w-30" :class="{'w-full': modal}">
			<tbody>
			<tr v-if="props.allow_collapse" @click.prevent="toggle">
				<td colspan="2" class="value !p-0 cursor-pointer" >
					<div class="flex flex-row justify-between whitespace-nowrap items-center">
						<span class="whitespace-nowrap text-sm font-bold pl-1">{{ collapsed ? "Click to Show" : "Click to Hide" }}</span>
						<span>
							<img v-if="!collapsed" src="@/assets/images/icon/minus.svg" alt="-" title="Collapse" class="h-4" >
							<img v-else src="@/assets/images/icon/plus.svg" alt="+" title="Expand" class="h-4" >
						</span>
					</div>
				</td>
			</tr>
			<tr v-for="key in displayKeys" :key="key" v-show="!collapsed || !allow_collapse">
				<!-- Key -->
				<td class="key" :class="{'min-w-30 break-all': narrow_key}" v-if="!props.hide_key">
					{{ key }}
				</td>

				<!-- Value -->
				<td v-if="data[key] != null && typeof data[key] === 'object' && Object.keys(data[key]).length > 0"
					 class="value text-sm !px-1 !py-1 min-w-30">
						<ObjectViewer :data="data[key]" :modal="modal" :allow_collapse="props.allow_collapse" />
				</td>
				<td v-else class="value text-sm !px-1 !py-0 min-w-30">
					{{ formatValue(data[key]) }}
				</td>
			</tr>
			</tbody>
		</table>
	</div>

</template>

<style scoped>
.object-viewer .key {
	background: #f5f5f5;
	@apply text-sm w-px !px-1 !py-0 align-top font-bold;/* w-px - forces key column to fit content *//* min-w-30 break-all add to make key smaller */
	@apply !border !border-solid !border-neutral-subtle !border-opacity-50;
}
.value {
	border: 1px solid #ddd;
}
</style>

